import { FieldPath, FieldValues, get, useController } from 'react-hook-form';
import { FormField, FormFieldProps } from '@britishcouncil/react-admin';
import { MultipleColumnDropdown, MultipleColumnDropdownProps } from '../MultipleColumnDropdown';
import { useFormContext } from './FormContext';
import { RHFormFieldProps } from './types';
import { ReadonlyFormValue } from './ReadonlyFormValue';

type RHFormMultipleColumnDropdownProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = RHFormFieldProps<FormFieldProps, TFieldValues, TName> &
  Omit<MultipleColumnDropdownProps, 'onChange' | 'value'> & {
    readonlyMode?: boolean;
    options: MultipleColumnDropdownProps['options'];
    onChange?: (value: MultipleColumnDropdownProps['value']) => void;
  };

export const RHFormMultipleColumnDropdown = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  name,
  control,
  required,
  disabled,
  rules,
  defaultValue,
  shouldUnregister,
  readonlyMode,
  onChange,
  ...props
}: RHFormMultipleColumnDropdownProps<TFieldValues, TName>) => {
  const { mode: formMode } = useFormContext();
  const { field, formState } = useController({
    name,
    control,
    rules,
    defaultValue,
    shouldUnregister,
  });

  const handleChange = (value: MultipleColumnDropdownProps['value']) => {
    field.onChange(value);
    onChange?.(value);
  };

  if (formMode === 'read' || readonlyMode) {
    return (
      <ReadonlyFormValue
        label={props.label}
        value={field.value}
        renderValue={(value) => {
          if (!value) return '-';
          return (
            props.options.flatMap((group) => group.data).find((item) => item.value === value)
              ?.text || '-'
          );
        }}
      />
    );
  }

  return (
    <FormField
      required={required}
      disabled={disabled}
      control={MultipleColumnDropdown}
      touched={true}
      error={get(formState.errors, name)?.message}
      {...field}
      {...props}
      onChange={handleChange}
    />
  );
};
